import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from "recharts";

const PERIODS = [
  { key: "1m", label: "Aylık" },
  { key: "3m", label: "3 Aylık" },
  { key: "6m", label: "6 Aylık" },
  { key: "1y", label: "Yıllık" },
  { key: "ytd", label: "Bu Yıl" },
];

const COLORS = ["#064E3B", "#10B981", "#F59E0B", "#EF4444", "#2563EB", "#7C3AED", "#DB2777"];

export default function Reports() {
  const [period, setPeriod] = useState("ytd");
  const { data } = useQuery({
    queryKey: ["reports", period],
    queryFn: async () => (await api.get(`/reports?period=${period}`)).data,
  });

  if (!data) return <div className="p-10 text-sm text-muted-foreground">Yükleniyor...</div>;
  const { rows, totals, income_categories, expense_categories } = data;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline">Finansal Rapor</div>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Gelir & Gider Analizi</h1>
        </div>
        <div className="flex rounded-md border border-border bg-white p-1" data-testid="period-tabs">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`period-${p.key}`}
              className={`px-4 py-1.5 text-sm rounded transition-colors duration-150 ${
                period === p.key ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 border-l border-t border-border/60 sm:grid-cols-2 lg:grid-cols-5">
        <div className="border-r border-b border-border/60 bg-white p-6">
          <div className="overline">Resmi Gelir (TL)</div>
          <div className="mono mt-3 text-2xl font-semibold text-emerald-700" data-testid="totals-income-resmi">
            {fmtMoney(totals.income_resmi || 0)}
          </div>
        </div>
        <div className="border-r border-b border-border/60 bg-white p-6">
          <div className="overline">Açıktan Gelir (TL)</div>
          <div className="mono mt-3 text-2xl font-semibold text-emerald-700/70" data-testid="totals-income-aciktan">
            {fmtMoney(totals.income_aciktan || 0)}
          </div>
        </div>
        <div className="border-r border-b border-border/60 bg-white p-6">
          <div className="overline">Resmi Gider (TL)</div>
          <div className="mono mt-3 text-2xl font-semibold text-red-600" data-testid="totals-expense-resmi">
            {fmtMoney(totals.expense_resmi || 0)}
          </div>
        </div>
        <div className="border-r border-b border-border/60 bg-white p-6">
          <div className="overline">Açıktan Gider (TL)</div>
          <div className="mono mt-3 text-2xl font-semibold text-red-600/70" data-testid="totals-expense-aciktan">
            {fmtMoney(totals.expense_aciktan || 0)}
          </div>
        </div>
        <div className="border-r border-b border-border/60 bg-white p-6">
          <div className="overline">Net (TL)</div>
          <div className={`mono mt-3 text-2xl font-semibold ${totals.net >= 0 ? "text-primary" : "text-red-600"}`}>
            {fmtMoney(totals.net)}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-md border border-border bg-white p-6">
        <div className="overline mb-4">Aylık Trend (TL)</div>
        <div className="h-80">
          <ResponsiveContainer>
            <BarChart data={rows}>
              <CartesianGrid stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="income" name="Gelir" fill="hsl(158,79%,17%)" />
              <Bar dataKey="expense" name="Gider" fill="hsl(0,84%,60%)" />
              <Bar dataKey="net" name="Net" fill="hsl(38,92%,50%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-md border border-border bg-white p-6">
          <div className="overline mb-4">Gelir Kategorileri</div>
          {income_categories.length ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="h-56">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={income_categories} dataKey="total" nameKey="category" outerRadius={70}>
                      {income_categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-2 text-sm">
                {income_categories.map((c, i) => (
                  <li key={c.category} className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-3 w-3" style={{ background: COLORS[i % COLORS.length] }} />
                      {c.category}
                    </span>
                    <span className="mono">{fmtMoney(c.total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-muted-foreground">Veri yok.</div>
          )}
        </div>
        <div className="rounded-md border border-border bg-white p-6">
          <div className="overline mb-4">Gider Kategorileri</div>
          {expense_categories.length ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="h-56">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={expense_categories} dataKey="total" nameKey="category" outerRadius={70}>
                      {expense_categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-2 text-sm">
                {expense_categories.map((c, i) => (
                  <li key={c.category} className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-3 w-3" style={{ background: COLORS[i % COLORS.length] }} />
                      {c.category}
                    </span>
                    <span className="mono">{fmtMoney(c.total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-muted-foreground">Veri yok.</div>
          )}
        </div>
      </div>
    </div>
  );
}
